import React from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { MANUAL_SCENES, SCENE_MAP, randomTrack, trackName } from '../../lib/ambience';

export default function MusicPanel() {
  const { musicState, saveMusicState } = useCampaign();
  const { scene: activeScene, playing, volume, trackUrl, loopMode = 'playlist', playlistIndex = 0 } = musicState;

  const activeSceneData = activeScene ? SCENE_MAP[activeScene] : null;

  const handleSceneClick = (sceneId) => {
    const scene = SCENE_MAP[sceneId];
    if (activeScene === sceneId && playing) {
      // Same scene — shuffle to a different track
      const newTrack = randomTrack(sceneId, trackUrl);
      const idx = scene.tracks.indexOf(newTrack);
      saveMusicState({ ...musicState, trackUrl: newTrack, playlistIndex: idx >= 0 ? idx : 0 });
    } else {
      // New scene — pick random track and start playing
      const newTrack = randomTrack(sceneId);
      const idx = scene.tracks.indexOf(newTrack);
      saveMusicState({
        scene: sceneId, playing: true, volume, trackUrl: newTrack,
        loopMode: 'playlist', playlistIndex: idx >= 0 ? idx : 0,
        previousScene: null, previousTrackUrl: null,
      });
    }
  };

  const handlePlayPause = () => {
    saveMusicState({ ...musicState, playing: !playing });
  };

  const handleStop = () => {
    saveMusicState({
      scene: null, playing: false, volume, trackUrl: null,
      loopMode: 'playlist', playlistIndex: 0,
      previousScene: null, previousTrackUrl: null,
    });
  };

  const handleVolume = (e) => {
    saveMusicState({ ...musicState, volume: parseFloat(e.target.value) });
  };

  const handleShuffle = () => {
    if (!activeScene) return;
    const scene = SCENE_MAP[activeScene];
    const newTrack = randomTrack(activeScene, trackUrl);
    const idx = scene.tracks.indexOf(newTrack);
    saveMusicState({ ...musicState, trackUrl: newTrack, playlistIndex: idx >= 0 ? idx : 0 });
  };

  const handleTrackSelect = (e) => {
    const url = e.target.value;
    const scene = SCENE_MAP[activeScene];
    const idx = scene?.tracks.indexOf(url) ?? 0;
    saveMusicState({ ...musicState, trackUrl: url, playlistIndex: idx >= 0 ? idx : 0, playing: true });
  };

  const handleLoopMode = () => {
    const modes = ['playlist', 'track', 'none'];
    const next = modes[(modes.indexOf(loopMode) + 1) % modes.length];
    saveMusicState({ ...musicState, loopMode: next });
  };

  const loopIcon = loopMode === 'track' ? '🔂' : loopMode === 'playlist' ? '🔁' : '➡️';
  const loopLabel = loopMode === 'track' ? 'Loop Track' : loopMode === 'playlist' ? 'Loop Playlist' : 'Play Once';

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.title}>Music</span>
      </div>

      {/* Now playing */}
      <div style={styles.nowPlaying}>
        {activeSceneData && playing ? (
          <>
            <span style={styles.nowIcon}>{activeSceneData.icon}</span>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={styles.nowLabel}>{activeSceneData.label}</div>
              <div style={styles.nowTrack}>{trackName(trackUrl)}</div>
            </div>
          </>
        ) : (
          <span style={styles.nowLabel}>No music playing</span>
        )}
      </div>

      {/* Track dropdown */}
      {activeSceneData && (
        <select style={styles.trackSelect} value={trackUrl || ''} onChange={handleTrackSelect}>
          {activeSceneData.tracks.map((t, i) => (
            <option key={t} value={t}>{i + 1}. {trackName(t)}</option>
          ))}
        </select>
      )}

      {/* Transport controls */}
      <div style={styles.transport}>
        <button style={{ ...styles.transportBtn, ...(playing ? styles.transportActive : {}) }} onClick={handlePlayPause} title={playing ? 'Pause' : 'Play'}>
          {playing ? '⏸' : '▶'}
        </button>
        <button style={styles.transportBtn} onClick={handleStop} title="Stop" disabled={!activeScene}>⏹</button>
        <button style={styles.transportBtn} onClick={handleShuffle} title="Shuffle track" disabled={!activeScene}>🔀</button>
        <button
          style={{ ...styles.transportBtn, ...(loopMode !== 'none' ? styles.transportActive : {}) }}
          onClick={handleLoopMode}
          title={loopLabel}
        >
          {loopIcon}
        </button>
      </div>

      {/* Volume */}
      <div style={styles.volumeRow}>
        <span style={styles.volumeLabel}>Vol</span>
        <input
          type="range" min={0} max={1} step={0.05}
          value={volume}
          onChange={handleVolume}
          style={styles.volumeSlider}
        />
        <span style={styles.volumeValue}>{Math.round(volume * 100)}%</span>
      </div>

      {/* Scene grid — only manual scenes */}
      <div style={styles.sceneGrid}>
        {MANUAL_SCENES.map((s) => (
          <button
            key={s.id}
            style={{
              ...styles.sceneBtn,
              borderColor: activeScene === s.id ? s.color : '#4a3d28',
              background: activeScene === s.id ? `${s.color}30` : '#0e1528',
            }}
            onClick={() => handleSceneClick(s.id)}
          >
            <span style={styles.sceneIcon}>{s.icon}</span>
            <span style={styles.sceneLabel}>{s.label}</span>
            <span style={styles.sceneCount}>{s.tracks.length} tracks</span>
          </button>
        ))}
      </div>

      <div style={styles.hint}>
        Click a scene to play. Click again to shuffle. Plays on Display view only.
        <br />Combat music auto-plays on battle start/end.
      </div>
    </div>
  );
}

const styles = {
  panel:          { padding: 12, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: 1 },
  header:         { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title:          { fontSize: 14, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", color: '#d4a847' },

  nowPlaying:     { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4 },
  nowIcon:        { fontSize: 20 },
  nowLabel:       { fontSize: 13, color: '#f0e6d0', fontWeight: 600 },
  nowTrack:       { fontSize: 11, color: '#9a8b6e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  trackSelect:    { width: '100%', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', fontSize: 12, padding: '6px 8px', outline: 'none', cursor: 'pointer' },

  transport:      { display: 'flex', gap: 6 },
  transportBtn:   { flex: 1, padding: '8px 0', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', fontSize: 18, cursor: 'pointer', textAlign: 'center' },
  transportActive:{ background: '#1a2a48', borderColor: '#d4a847' },

  volumeRow:      { display: 'flex', alignItems: 'center', gap: 8 },
  volumeLabel:    { fontSize: 11, color: '#9a8b6e', fontWeight: 600, width: 28 },
  volumeSlider:   { flex: 1, height: 6, WebkitAppearance: 'none', appearance: 'none', background: '#4a3d28', borderRadius: 3, outline: 'none', cursor: 'pointer' },
  volumeValue:    { fontSize: 11, color: '#9a8b6e', width: 32, textAlign: 'right' },

  sceneGrid:      { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 },
  sceneBtn:       { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '10px 4px', border: '2px solid #4a3d28', borderRadius: 6, cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' },
  sceneIcon:      { fontSize: 22 },
  sceneLabel:     { fontSize: 11, fontWeight: 700, color: '#f0e6d0' },
  sceneCount:     { fontSize: 9, color: '#6a5d45' },

  hint:           { fontSize: 10, color: '#6a5d45', textAlign: 'center', lineHeight: 1.4 },
};
